// src/pages/agent-booking-system-sabre-integration/components/RankingEngine.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabaseQueries } from '../../../utils/supabase';

const RankingEngine = ({ tenant, searchResults, onResultsRanked }) => {
  const [rankingConfig, setRankingConfig] = useState({
    cost_weightage: 0.40,
    duration_weightage: 0.25,
    carbon_weightage: 0.20,
    preference_weightage: 0.15
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastRanking, setLastRanking] = useState(null);

  useEffect(() => {
    loadRankingConfiguration();
  }, [tenant]);

  const loadRankingConfiguration = async () => {
    if (!tenant?.id) return;
    
    try {
      // In a real app, this would load from the ranking_configurations table
      // For now, using default values
      console.log('Loading ranking configuration for tenant:', tenant?.id);
    } catch (error) {
      console.error('Error loading ranking configuration:', error);
    }
  };

  const handleWeightageChange = (field, value) => {
    const numValue = parseFloat(value) / 100; // Convert percentage to decimal
    setRankingConfig(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const normalizeWeightages = () => {
    const total = Object.values(rankingConfig)?.reduce((sum, val) => sum + val, 0);
    if (total === 0) return rankingConfig;
    
    return Object.fromEntries(
      Object.entries(rankingConfig)?.map(([key, value]) => [key, value / total])
    );
  };

  const calculateScore = (flight, normalizedWeights) => {
    // Normalize values to 0-1 scale for scoring
    const maxCost = Math.max(...(searchResults?.flights || [])?.map(f => f?.fare_amount || 0));
    const maxDuration = Math.max(...(searchResults?.flights || [])?.map(f => 
      new Date(f.arrival_time) - new Date(f.departure_time)
    ));
    
    // Cost score (lower cost = higher score)
    const costScore = maxCost > 0 ? 1 - ((flight?.fare_amount || 0) / maxCost) : 0.5;
    
    // Duration score (shorter duration = higher score)
    const flightDuration = new Date(flight.arrival_time) - new Date(flight.departure_time);
    const durationScore = maxDuration > 0 ? 1 - (flightDuration / maxDuration) : 0.5;
    
    // Carbon score (mock - in real app would come from carbon footprint data)
    const carbonScore = Math.random() * 0.3 + 0.7; // Mock: 0.7-1.0 range
    
    // Preference score (mock - based on airline, class, etc.)
    const preferenceScore = flight?.airline_code === 'AA' ? 0.9 : 
                           flight?.airline_code === 'DL' ? 0.8 : 0.6;
    
    // Calculate weighted score
    const totalScore = (
      costScore * normalizedWeights?.cost_weightage +
      durationScore * normalizedWeights?.duration_weightage +
      carbonScore * normalizedWeights?.carbon_weightage +
      preferenceScore * normalizedWeights?.preference_weightage
    );
    
    return {
      total: Math.round(totalScore * 100) / 100,
      breakdown: {
        cost: Math.round(costScore * 100) / 100,
        duration: Math.round(durationScore * 100) / 100,
        carbon: Math.round(carbonScore * 100) / 100,
        preference: Math.round(preferenceScore * 100) / 100
      }
    };
  };

  const rankResults = async () => {
    if (!searchResults?.flights?.length) return;
    
    setIsProcessing(true);
    
    try {
      const normalizedWeights = normalizeWeightages();
      
      // Calculate scores for each flight
      const rankedFlights = searchResults?.flights?.map(flight => {
        const scores = calculateScore(flight, normalizedWeights);
        return {
          ...flight,
          ranking_score: scores?.total,
          ranking_factors: {
            weights: normalizedWeights,
            scores: scores?.breakdown
          }
        };
      })?.sort((a, b) => b?.ranking_score - a?.ranking_score);
      
      // Update results with rankings
      const updatedResults = {
        ...searchResults,
        flights: rankedFlights
      };
      
      onResultsRanked?.(updatedResults);
      
      setLastRanking({
        timestamp: new Date(),
        flightsProcessed: rankedFlights?.length,
        topScore: rankedFlights?.[0]?.ranking_score || 0,
        averageScore: rankedFlights?.reduce((sum, f) => sum + f?.ranking_score, 0) / rankedFlights?.length
      });
      
    } catch (error) {
      console.error('Error ranking results:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetToDefaults = () => {
    setRankingConfig({
      cost_weightage: 0.40,
      duration_weightage: 0.25,
      carbon_weightage: 0.20,
      preference_weightage: 0.15
    });
  };

  const saveConfiguration = async () => {
    if (!tenant?.id) return;
    
    try {
      // In a real app, this would save to ranking_configurations table
      console.log('Saving ranking configuration:', rankingConfig);
      // await supabaseQueries.updateRankingConfiguration(tenant.id, rankingConfig);
    } catch (error) {
      console.error('Error saving ranking configuration:', error);
    }
  };

  const totalPercentage = Math.round(
    Object.values(rankingConfig)?.reduce((sum, val) => sum + val, 0) * 100
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-heading-bold text-text-primary mb-2">
          Intelligent Ranking Engine
        </h1>
        <p className="text-text-secondary">
          Configure ranking algorithms to optimize travel options based on corporate policies and preferences
        </p>
      </div>
      {/* Configuration Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Weightage Configuration */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-heading-semibold text-text-primary">
              Ranking Weights
            </h2>
            <div className={`text-sm font-body-medium px-3 py-1 rounded-full ${
              totalPercentage === 100 
                ? 'bg-green-100 text-green-800' :'bg-orange-100 text-orange-800'
            }`}>
              Total: {totalPercentage}%
            </div>
          </div>

          <div className="space-y-6">
            {/* Cost Weight */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="DollarSign" size={16} className="text-green-600" />
                  <label className="text-sm font-body-medium text-text-primary">
                    Cost Optimization
                  </label>
                </div>
                <span className="text-sm text-text-secondary">
                  {Math.round(rankingConfig?.cost_weightage * 100)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(rankingConfig?.cost_weightage * 100)}
                onChange={(e) => handleWeightageChange('cost_weightage', e?.target?.value)}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <p className="text-xs text-text-secondary mt-1">
                Prioritize lower-cost options
              </p>
            </div>

            {/* Duration Weight */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-blue-600" />
                  <label className="text-sm font-body-medium text-text-primary">
                    Travel Duration
                  </label>
                </div>
                <span className="text-sm text-text-secondary">
                  {Math.round(rankingConfig?.duration_weightage * 100)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(rankingConfig?.duration_weightage * 100)}
                onChange={(e) => handleWeightageChange('duration_weightage', e?.target?.value)}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <p className="text-xs text-text-secondary mt-1">
                Favor shorter travel times
              </p>
            </div>

            {/* Carbon Footprint Weight */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Leaf" size={16} className="text-green-600" />
                  <label className="text-sm font-body-medium text-text-primary">
                    Carbon Footprint
                  </label>
                </div>
                <span className="text-sm text-text-secondary">
                  {Math.round(rankingConfig?.carbon_weightage * 100)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(rankingConfig?.carbon_weightage * 100)}
                onChange={(e) => handleWeightageChange('carbon_weightage', e?.target?.value)}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <p className="text-xs text-text-secondary mt-1">
                Prioritize eco-friendly options
              </p>
            </div>

            {/* Preference Weight */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Star" size={16} className="text-purple-600" />
                  <label className="text-sm font-body-medium text-text-primary">
                    Corporate Preferences
                  </label>
                </div>
                <span className="text-sm text-text-secondary">
                  {Math.round(rankingConfig?.preference_weightage * 100)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(rankingConfig?.preference_weightage * 100)}
                onChange={(e) => handleWeightageChange('preference_weightage', e?.target?.value)}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <p className="text-xs text-text-secondary mt-1">
                Favor preferred suppliers and amenities
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 mt-6 pt-6 border-t border-border">
            <button
              onClick={resetToDefaults}
              className="flex-1 px-4 py-2 text-sm font-body-medium text-text-secondary border border-border rounded-lg hover:bg-secondary-50 transition-colors"
            >
              Reset to Defaults
            </button>
            <button
              onClick={saveConfiguration}
              className="flex-1 px-4 py-2 text-sm font-body-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Save Configuration
            </button>
          </div>
        </div>

        {/* Results & Analytics */}
        <div className="space-y-6">
          {/* Ranking Controls */}
          <div className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-lg font-heading-semibold text-text-primary mb-4">
              Apply Ranking
            </h2>
            
            <div className="space-y-4">
              <div className="text-sm text-text-secondary">
                <p className="mb-2">
                  Current search results: {searchResults?.flights?.length || 0} flights
                </p>
                {totalPercentage !== 100 && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertTriangle" size={16} className="text-orange-600" />
                      <span className="text-orange-800 text-xs">
                        Weights should total 100% for optimal ranking
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={rankResults}
                disabled={isProcessing || !searchResults?.flights?.length || totalPercentage === 0}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary-600 text-white font-body-medium rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Zap" size={16} />
                    <span>Rank Results</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Last Ranking Stats */}
          {lastRanking && (
            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
                Last Ranking Results
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Processed:</span>
                  <span className="text-text-primary font-body-medium">
                    {lastRanking?.flightsProcessed} flights
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Top Score:</span>
                  <span className="text-text-primary font-body-medium">
                    {(lastRanking?.topScore * 100)?.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Average Score:</span>
                  <span className="text-text-primary font-body-medium">
                    {(lastRanking?.averageScore * 100)?.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Processed At:</span>
                  <span className="text-text-primary font-body-medium">
                    {lastRanking?.timestamp?.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Algorithm Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-body-medium text-blue-900 mb-2">
                  How Ranking Works
                </h4>
                <div className="text-xs text-blue-700 space-y-1">
                  <p>• Results are scored based on your configured weights</p>
                  <p>• Each factor is normalized to ensure fair comparison</p>
                  <p>• Corporate policies automatically influence preferences</p>
                  <p>• Sabre data provides real-time availability and pricing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingEngine;